/**
 * Palette to Token Converter — SPA adapter.
 *
 * Wraps @chromis/shared-logic's token conversion as a static class
 * to preserve the existing PaletteToTokenConverter.convertPalette() interface.
 */

import {
  generateTokenSet,
  validateTokenSet,
  type TokenSet,
  type TokenValidationResult,
} from '@chromis/shared-logic';
import { PaletteRecord } from './database';

interface TokenGenerationOptions {
  namingConvention?: 'semantic' | 'systematic' | 'camelCase' | 'kebab-case' | 'snake_case';
  includeSemanticTokens?: boolean;
  includeAliases?: boolean;
}

// Re-export types consumers need
export type {
  TokenSet,
  TokenValidationResult,
} from '@chromis/shared-logic';

export class PaletteToTokenConverter {
  static convertPalette(
    palette: PaletteRecord,
    options: Partial<TokenGenerationOptions> = {},
  ): TokenSet {
    const names = palette.colors.map((_, i) => `color-${i + 1}`);
    return generateTokenSet(palette.colors, names, {
      namingConvention: options.namingConvention ?? 'systematic',
      includeSemanticTokens: options.includeSemanticTokens ?? true,
      includeAliases: options.includeAliases ?? true,
      paletteName: palette.name,
    });
  }

  static validateTokens(tokenSet: TokenSet): TokenValidationResult {
    return validateTokenSet(tokenSet);
  }

  // Legacy private helpers accessed via bracket notation in tokenEngineIntegration
  static hexToColorValue(hex: string): { colorSpace: string; channels: [number, number, number]; alpha?: number } {
    const clean = hex.replace('#', '');
    let r: number, g: number, b: number;

    if (clean.length === 3) {
      r = parseInt(clean[0] + clean[0], 16) / 255;
      g = parseInt(clean[1] + clean[1], 16) / 255;
      b = parseInt(clean[2] + clean[2], 16) / 255;
    } else {
      r = parseInt(clean.substring(0, 2), 16) / 255;
      g = parseInt(clean.substring(2, 4), 16) / 255;
      b = parseInt(clean.substring(4, 6), 16) / 255;
    }

    return { colorSpace: 'srgb', channels: [r, g, b] };
  }

  static getHue(hex: string): string | null {
    const { channels } = PaletteToTokenConverter.hexToColorValue(hex);
    const [r, g, b] = channels;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta === 0) return null;

    let hue: number;
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;

    hue = Math.round(hue * 60);
    if (hue < 0) hue += 360;

    if ((hue >= 0 && hue < 30) || (hue >= 330 && hue <= 360)) return 'red';
    if (hue >= 30 && hue < 60) return 'orange';
    if (hue >= 60 && hue < 90) return 'yellow';
    if (hue >= 90 && hue < 150) return 'green';
    if (hue >= 150 && hue < 210) return 'cyan';
    if (hue >= 210 && hue < 270) return 'blue';
    if (hue >= 270 && hue < 330) return 'purple';
    return null;
  }
}
